import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

// Track if we've already shown quota error to avoid spam
let quotaErrorShown = false;

// Track keys that have failed to save due to quota (to skip auto-save for them)
const failedKeys = new Set<string>();

/**
 * Check if error is QuotaExceededError
 */
function isQuotaExceededError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'QuotaExceededError' ||
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

/**
 * Attempt to free up localStorage space by removing non-critical data
 */
function attemptToFreeSpace(): boolean {
  try {
    const keysToRemove: string[] = [];
    const allKeys: { key: string; size: number }[] = [];
    
    // Collect all keys with their sizes
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const item = localStorage.getItem(key);
        const size = item ? item.length : 0;
        allKeys.push({ key, size });
      }
    }
    
    // Sort by size (largest first) to remove big items first
    allKeys.sort((a, b) => b.size - a.size);
    
    console.log('📊 LocalStorage keys by size:', allKeys.map(k => `${k.key}: ${(k.size / 1024).toFixed(1)}KB`));
    
    // Strategy 1: Remove OLD individual keys that were migrated to consolidated keys
    const oldIndividualKeys = [
      'blocksystem-blocks',
      'blocksystem-connections',
      'blocksystem-connectionmarkers',
      'blocksystem-systems',
      'blocksystem-textannotations',
      'blocksystem-placedlabels',
      'blocksystem-pipetypes',
      'blocksystem-labeltemplates',
      'blocksystem-projectname',
      'blocksystem-selectedpipetype',
      'blocksystem-drawingfolder',
      'blocksystem-activedrawingid',
      'blocksystem-plans',
      'blocksystem-titleblocktemplates',
      'blocksystem-activeplanid',
      'blocksystem-viewstate',
      'blocksystem-backgroundsettings',
      'blocksystem-viewdisplaysettings',
      'blocksystem-showdimensions',
      'blocksystem-snapenabled',
    ];
    
    for (const oldKey of oldIndividualKeys) {
      if (localStorage.getItem(oldKey) !== null) {
        keysToRemove.push(oldKey);
        const size = allKeys.find(k => k.key === oldKey)?.size || 0;
        console.log(`🔄 Migrating old key: ${oldKey} (${(size / 1024).toFixed(1)}KB)`);
      }
    }
    
    // Strategy 2: Remove old temporary/cache data
    for (const { key } of allKeys) {
      if (key.includes('temp-') || key.includes('cache-') || key.includes('old-')) {
        keysToRemove.push(key);
      }
    }
    
    // Strategy 3: If not enough, remove large auto-save data from other modules/sessions
    // Keep only essential keys for current session
    if (keysToRemove.length === 0) {
      const essentialPrefixes = [
        'clerk',  // Auth data
        'loglevel', // Logging
      ];
      
      for (const { key } of allKeys) {
        const isEssential = essentialPrefixes.some(prefix => key.toLowerCase().startsWith(prefix));
        
        // Remove non-essential large items (> 100KB)
        if (!isEssential && allKeys.find(k => k.key === key)!.size > 100000) {
          keysToRemove.push(key);
          console.log(`🎯 Targeting large non-essential key: ${key} (${(allKeys.find(k => k.key === key)!.size / 1024).toFixed(1)}KB)`);
        }
      }
    }
    
    // Strategy 4: If still not enough, remove HALF of all auto-save data
    // This is aggressive but necessary when quota is exceeded
    if (keysToRemove.length === 0) {
      console.warn('⚠️ Using aggressive cleanup - removing 50% of auto-save data');
      const autoSaveKeys = allKeys.filter(({ key }) => 
        key.includes('blocksystem-') || 
        key.includes('isoblock-') || 
        key.includes('systemtable-')
      );
      
      // Remove every other auto-save key (keeping half)
      autoSaveKeys.forEach(({ key }, index) => {
        if (index % 2 === 0) {
          keysToRemove.push(key);
        }
      });
    }
    
    // Remove identified keys
    let totalFreed = 0;
    keysToRemove.forEach(key => {
      try {
        const item = localStorage.getItem(key);
        const size = item ? item.length : 0;
        localStorage.removeItem(key);
        totalFreed += size;
        console.log(`🗑️ Freed ${(size / 1024).toFixed(1)}KB by removing: ${key}`);
      } catch (e) {
        console.error(`Failed to remove ${key}:`, e);
      }
    });
    
    console.log(`✅ Total freed: ${(totalFreed / 1024).toFixed(1)}KB (${keysToRemove.length} keys removed)`);
    
    return keysToRemove.length > 0;
  } catch (error) {
    console.error('Failed to free localStorage space:', error);
    return false;
  }
}

/**
 * Get approximate localStorage usage in MB
 */
function getLocalStorageSize(): number {
  let total = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      const item = localStorage.getItem(key);
      if (item) {
        total += key.length + item.length;
      }
    }
  }
  // Convert to MB (characters are roughly 2 bytes in UTF-16)
  return (total * 2) / (1024 * 1024);
}

/**
 * Auto-save hook - saves data to localStorage with debounce
 */
export function useAutoSave<T>(
  key: string,
  data: T,
  interval: number = 3000 // 3 seconds
) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedRef = useRef<string>('');
  const isInitialMount = useRef(true);
  const dataRef = useRef(data);

  // Keep ref synced with latest data
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Debounced auto-save effect
  useEffect(() => {
    // Skip first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const saveData = (currentData: T) => {
      const serialized = JSON.stringify(currentData);
      
      // Only save if data has actually changed
      if (serialized !== lastSavedRef.current) {
        try {
          localStorage.setItem(key, serialized);
          lastSavedRef.current = serialized;
          console.log(`💾 Auto-saved ${key}`);
          // Reset error flag on successful save
          quotaErrorShown = false;
        } catch (error) {
          console.error('Failed to auto-save:', error);
          
          // Handle QuotaExceededError specifically
          if (isQuotaExceededError(error)) {
            if (!quotaErrorShown) {
              quotaErrorShown = true;
              const size = getLocalStorageSize().toFixed(2);
              console.warn(`⚠️ LocalStorage quota exceeded (~${size} MB used)`);
              
              // Try to free space
              const freedSpace = attemptToFreeSpace();
              
              if (freedSpace) {
                toast.warning('Speicher voll - alte Daten wurden gelöscht', {
                  description: 'Bitte speichern Sie Ihr Projekt als Datei.',
                  duration: 8000,
                });
                
                // Retry save after freeing space
                try {
                  localStorage.setItem(key, serialized);
                  lastSavedRef.current = serialized;
                  console.log(`💾 Auto-saved ${key} after freeing space`);
                } catch (retryError) {
                  if (isQuotaExceededError(retryError)) {
                    toast.error('Speicher voll!', {
                      description: 'Bitte speichern Sie Ihr Projekt als Datei (Strg+S) und reduzieren Sie die Projektgröße.',
                      duration: 10000,
                    });
                  }
                }
              } else {
                toast.error('Speicher voll!', {
                  description: 'Bitte speichern Sie Ihr Projekt als Datei (Strg+S) und reduzieren Sie die Projektgröße.',
                  duration: 10000,
                });
              }
            }
          } else {
            // Other errors
            toast.error('Auto-Save fehlgeschlagen');
          }
        }
      }
    };

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Schedule new save
    timeoutRef.current = setTimeout(() => {
      saveData(data);
    }, interval);

    // On cleanup (re-render), just clear the timeout
    // We do NOT save here, otherwise we lose the "debounce" benefit
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [key, data, interval]);

  // Unmount effect - Force save on unmount
  useEffect(() => {
    return () => {
      // On unmount, if there's pending data (or just to be safe), save the latest data
      // We check against lastSavedRef inside the logic to avoid redundant writes
      const currentData = dataRef.current;
      const serialized = JSON.stringify(currentData);
      
      if (serialized !== lastSavedRef.current && !isInitialMount.current) {
        try {
          console.log(`💾 Force-saving on unmount: ${key}`);
          localStorage.setItem(key, serialized);
          lastSavedRef.current = serialized;
        } catch (error) {
          console.error('Failed to save on unmount:', error);
          // Don't show toast on unmount as user might be navigating away
          if (isQuotaExceededError(error)) {
            console.warn('⚠️ QuotaExceededError on unmount - data not saved');
          }
        }
      }
    };
  }, [key]); // Only run on mount/unmount (and key change)
}

/**
 * Load saved data from localStorage
 */
export function loadSavedData<T>(key: string, defaultValue: T): T {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      // Silent load - don't spam console
      return JSON.parse(saved) as T;
    }
  } catch (error) {
    console.error('Failed to load saved data:', error);
    // Clear corrupted data
    try {
      localStorage.removeItem(key);
      console.log(`🗑️ Cleared corrupted data for ${key}`);
    } catch (clearError) {
      console.error('Failed to clear corrupted data:', clearError);
    }
  }
  return defaultValue;
}

/**
 * Clear saved data from localStorage
 */
export function clearSavedData(key: string) {
  try {
    localStorage.removeItem(key);
    console.log(`🗑️ Cleared saved data for ${key}`);
  } catch (error) {
    console.error('Failed to clear saved data:', error);
  }
}